import { createBrowserRouter, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { MainLayout } from "./components/layout/MainLayout";
import { ProtectedRoute } from "./features/auth";
import { BrandSpinner } from "./components/brand";
import { AdminRoute } from "./features/admin/components/AdminRoute";
import { AdminLayout } from "./features/admin/components/AdminLayout";
import { AdminRouteError } from "./features/admin/components/AdminRouteError";

// Lazy-loaded pages
const HomePage = lazy(() =>
  import("./features/markets/routes/HomePage").then((m) => ({
    default: m.HomePage,
  })),
);
const MarketDetailPage = lazy(() =>
  import("./features/betting/routes/MarketDetailPage").then((m) => ({
    default: m.MarketDetailPage,
  })),
);
const ProfilePage = lazy(() =>
  import("./features/profile/routes/ProfilePage").then((m) => ({
    default: m.ProfilePage,
  })),
);
const PortfolioPage = lazy(() =>
  import("./features/portfolio/routes/PortfolioPage").then((m) => ({
    default: m.PortfolioPage,
  })),
);
const LeaderboardPage = lazy(() =>
  import("./features/profile/routes/LeaderboardPage").then((m) => ({
    default: m.LeaderboardPage,
  })),
);
const CreateMarketPage = lazy(() =>
  import("./features/markets/routes/CreateMarketPage").then((m) => ({
    default: m.CreateMarketPage,
  })),
);
const WorldCupPage = lazy(() =>
  import("./features/world-cup/routes/WorldCupPage").then((m) => ({
    default: m.WorldCupPage,
  })),
);
const PrivacyPage = lazy(() =>
  import("./features/legal/routes/PrivacyPage").then((m) => ({
    default: m.PrivacyPage,
  })),
);
const TermsPage = lazy(() =>
  import("./features/legal/routes/TermsPage").then((m) => ({
    default: m.TermsPage,
  })),
);

// Admin pages (lazy-loaded, separate layout)
const AdminDashboardPage = lazy(() =>
  import("./features/admin/routes/AdminDashboardPage").then((m) => ({
    default: m.AdminDashboardPage,
  })),
);
const AdminPendingPage = lazy(() =>
  import("./features/admin/routes/AdminPendingPage").then((m) => ({
    default: m.AdminPendingPage,
  })),
);
const AdminMarketsPage = lazy(() =>
  import("./features/admin/routes/AdminMarketsPage").then((m) => ({
    default: m.AdminMarketsPage,
  })),
);
const AdminUsersPage = lazy(() =>
  import("./features/admin/routes/AdminUsersPage").then((m) => ({
    default: m.AdminUsersPage,
  })),
);
const AdminHealthPage = lazy(() =>
  import("./features/admin/routes/AdminHealthPage").then((m) => ({
    default: m.AdminHealthPage,
  })),
);

function LazyPage({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-[60vh]">
          <BrandSpinner size="lg" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    element: <MainLayout />,
    children: [
      {
        path: "/",
        element: (
          <LazyPage>
            <HomePage />
          </LazyPage>
        ),
      },
      {
        path: "/market/:id",
        element: (
          <LazyPage>
            <MarketDetailPage />
          </LazyPage>
        ),
      },
      {
        path: "/profile",
        element: (
          <ProtectedRoute>
            <LazyPage>
              <ProfilePage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: "/portfolio",
        element: (
          <ProtectedRoute>
            <LazyPage>
              <PortfolioPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: "/leaderboard",
        element: (
          <LazyPage>
            <LeaderboardPage />
          </LazyPage>
        ),
      },
      {
        path: "/world-cup",
        element: (
          <LazyPage>
            <WorldCupPage />
          </LazyPage>
        ),
      },
      {
        // C14: permissionless market creation. ProtectedRoute gates on
        // authentication (wallet connected + JWT). The page itself also
        // checks `useAnchorWallet()` and renders a "connect first" prompt
        // if the wallet bridge hasn't yielded an Anchor-shaped adapter yet.
        path: "/create",
        element: (
          <ProtectedRoute>
            <LazyPage>
              <CreateMarketPage />
            </LazyPage>
          </ProtectedRoute>
        ),
      },
      {
        path: "/privacy",
        element: (
          <LazyPage>
            <PrivacyPage />
          </LazyPage>
        ),
      },
      {
        path: "/terms",
        element: (
          <LazyPage>
            <TermsPage />
          </LazyPage>
        ),
      },
      {
        path: "*",
        element: <Navigate to="/" replace />,
      },
    ],
  },
  {
    path: "/admin",
    element: (
      <AdminRoute>
        <AdminLayout />
      </AdminRoute>
    ),
    // Crashes in the admin shell itself (AdminRoute/AdminLayout) — full page.
    errorElement: <AdminRouteError />,
    children: [
      {
        // Pathless error boundary: a crashing admin page only swaps the
        // Outlet content; the chrome above stays mounted and navigable.
        errorElement: <AdminRouteError />,
        children: [
          {
            index: true,
            element: (
              <LazyPage>
                <AdminDashboardPage />
              </LazyPage>
            ),
          },
          {
            path: "pending",
            element: (
              <LazyPage>
                <AdminPendingPage />
              </LazyPage>
            ),
          },
          {
            path: "markets",
            element: (
              <LazyPage>
                <AdminMarketsPage />
              </LazyPage>
            ),
          },
          {
            path: "users",
            element: (
              <LazyPage>
                <AdminUsersPage />
              </LazyPage>
            ),
          },
          {
            path: "health",
            element: (
              <LazyPage>
                <AdminHealthPage />
              </LazyPage>
            ),
          },
        ],
      },
    ],
  },
]);
